import { useState, useEffect, useMemo, useRef } from 'react';
import { apiUrl } from '../config/api';
import { POLL_INTERVAL_MS, MAX_POLL_ATTEMPTS } from '../config/playback';

const TOC_TITLE_PATTERNS = [
  /^contents$/i,
  /^table of contents$/i,
  /^index$/i,
  /^illustrations$/i,
  /^list of illustrations$/i,
];

function isTocChapter(chapter) {
  if (!chapter?.title) return false;
  const t = chapter.title.trim();
  return TOC_TITLE_PATTERNS.some(re => re.test(t));
}

/**
 * Fetch manifest, poll during generation, parse chapters, filter TOC.
 * Returns manifest data and derived arrays (visibleChapters, allWords, hiddenRegions).
 */
export function useManifest(bookId) {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const initialSeekDoneRef = useRef(false);

  useEffect(() => {
    if (!bookId) {
      initialSeekDoneRef.current = false;
      setManifest(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let pollInterval = null;
    initialSeekDoneRef.current = false;

    async function fetchManifest() {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(apiUrl(`/api/books/${bookId}/manifest`));
        if (!res.ok) throw new Error(`Failed to load book (${res.status})`);

        const data = await res.json();
        if (cancelled) return;

        setManifest(data);
        setLoading(false);

        if (data.status === 'generating') {
          let pollCount = 0;
          pollInterval = setInterval(async () => {
            pollCount++;
            if (pollCount >= MAX_POLL_ATTEMPTS) {
              console.warn(`[useManifest] Polling stopped after ${MAX_POLL_ATTEMPTS} attempts for book ${bookId}`);
              clearInterval(pollInterval);
              if (!cancelled) {
                setError('Generation is taking longer than expected. Please refresh to check status.');
              }
              return;
            }
          try {
            const pollRes = await fetch(apiUrl(`/api/books/${bookId}/manifest`));
            if (pollRes.ok) {
              const updated = await pollRes.json();
                if (!cancelled) {
                  setManifest(updated);
                  if (updated.status === 'complete') clearInterval(pollInterval);
              }
            }
          } catch {
            // Manifest not ready yet while generation is still in progress.
          }
        }, POLL_INTERVAL_MS);
      }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    }

    fetchManifest();
    return () => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [bookId]);

  const visibleChapters = useMemo(() => {
    if (!manifest?.chapters) return [];
    return manifest.chapters.filter(ch => !isTocChapter(ch));
  }, [manifest]);

  const firstVisibleChapter = visibleChapters[0] || null;

  const allWords = useMemo(() => {
    if (!manifest?.chunks) return [];
    return manifest.chunks.flatMap(chunk => chunk.words);
  }, [manifest]);

  const hiddenRegions = useMemo(() => {
    if (!manifest?.chapters) return [];
    const regions = [];
    for (let i = 0; i < manifest.chapters.length; i++) {
      const ch = manifest.chapters[i];
      if (isTocChapter(ch)) {
        const next = manifest.chapters[i + 1];
        const end = next ? next.startTime : (manifest.totalDuration || 0);
        regions.push({ start: ch.startTime, end });
      }
    }
    return regions;
  }, [manifest]);

  return {
    manifest,
    loading,
    error,
    visibleChapters,
    firstVisibleChapter,
    allWords,
    hiddenRegions,
    initialSeekDoneRef,
  };
}
